<template>
  <div class="w-full input-group">
    <label
      v-if="label"
      :for="id"
      :class="[error ? labelError : labelValid]"
      class="w-full cursor-pointer text-sm pl-1"
      @click="editor.focus()"
    >{{ label }}</label>

    <div class="editor">
      <editor-menu-bar :editor="editor">
        <div class="editor-actions" slot-scope="{ commands, isActive, getMarkAttrs }">
          <button
            type="button"
            class="editor-action"
            :class="{ 'is-active': isActive.bold() }"
            @click="commands.bold"
          >
            <svgicon class="w-4 h-4" icon="bold"></svgicon>
          </button>

          <button
            type="button"
            class="editor-action"
            :class="{ 'is-active': isActive.italic() }"
            @click="commands.italic"
          >
            <svgicon class="w-4 h-4" icon="italic"></svgicon>
          </button>

          <button
            type="button"
            class="editor-action"
            :class="{ 'is-active': isActive.strike() }"
            @click="commands.strike"
          >
            <svgicon class="w-4 h-4" icon="strikethrough"></svgicon>
          </button>

          <button
            type="button"
            class="editor-action"
            :class="{ 'is-active': isActive.underline() }"
            @click="commands.underline"
          >
            <svgicon class="w-4 h-4" icon="underline"></svgicon>
          </button>

          <!-- <button
            type="button"
            class="editor-action"
            :class="{ 'is-active': isActive.spoiler() }"
            @click="commands.spoiler"
            v-if="!simple"
          >
            <svgicon class="w-4 h-4" icon="spoiler"></svgicon>
          </button>-->

          <span v-if="!simple" class="w-4"></span>

          <button
            type="button"
            class="editor-action"
            :class="{ 'is-active': isActive.bullet_list() }"
            @click="commands.bullet_list"
            v-if="!simple"
          >
            <svgicon class="w-4 h-4" icon="ul"></svgicon>
          </button>

          <button
            type="button"
            class="editor-action"
            :class="{ 'is-active': isActive.ordered_list() }"
            @click="commands.ordered_list"
            v-if="!simple"
          >
            <svgicon class="w-4 h-4" icon="ol"></svgicon>
          </button>

          <button
            type="button"
            class="editor-action"
            :class="{ 'is-active': isActive.blockquote() }"
            @click="commands.blockquote"
            v-if="!simple"
          >
            <svgicon class="w-4 h-4" icon="quote"></svgicon>
          </button>

          <span v-if="!simple" class="w-4"></span>

          <button
            type="button"
            class="editor-action"
            @click="showLinkModal(getMarkAttrs('link'))"
            v-if="!simple || (simple && includeLinks)"
          >
            <link-icon class="w-4 h-4 mt-0"></link-icon>
          </button>

          <button
            type="button"
            class="editor-action"
            @click="modals.image = true"
            v-if="!simple || (simple && includeImages)"
          >
            <image-icon class="w-4 h-4 mt-0"></image-icon>
          </button>

          <button type="button" class="editor-action" @click="modals.embed = true" v-if="!simple">
            <video-icon class="w-4 h-4 mt-0"></video-icon>
          </button>

          <button
            type="button"
            class="editor-action"
            @click="commands.insert"
            v-if="!simple || (simple && includeEmotes)"
          >
            <smile-icon class="w-4 h-4 mt-0"></smile-icon>
          </button>

          <span class="w-4"></span>

          <button type="button" class="editor-action" @click="commands.undo">
            <svgicon class="w-4 h-4" icon="undo"></svgicon>
          </button>

          <button type="button" class="editor-action" @click="commands.redo">
            <svgicon class="w-4 h-4" icon="redo"></svgicon>
          </button>

          <rich-text-embed-modal
            v-if="modals.embed"
            @close="modals.embed = false"
            @success="insertNoembed($event, commands.noembed)"
          />

          <rich-text-image-modal
            v-if="modals.image"
            @close="modals.image = false"
            @success="insertImage($event, commands.image)"
          />

          <rich-text-link-modal
            v-if="modals.link"
            @close="modals.link = false"
            @success="insertLink($event, commands.link)"
            :existing="getMarkAttrs('link')"
          />
        </div>
      </editor-menu-bar>

      <editor-content
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        class="outline-none w-full bg-grey-0 border focus:shadow-inner rounded py-4 px-2 text-grey-9 leading-tight shadow | richtext rich-content"
        :class="[error ? inputError : inputValid]"
        :editor="editor"
      />

      <div class="text-sm leading-none -mx-2 -my-1" v-show="showSuggestions" ref="suggestions">
        <template v-if="hasResults">
          <div class="w-full p-2" style="width: 18.5rem;">
            <div class="w-full flex flex-wrap">
              <div
                v-for="(emote, index) in visibleEmotes"
                :key="emote.name"
                class="p-1 m-1 rounded border border-transparent | emote-item"
                @click="selectEmote(emote)"
                :class="{ 'is-selected': navigatedEmoteIndex === index }"
              >
                <img class="w-7 h-7" :src="emote.url" />
              </div>
            </div>
          </div>
          <div
            v-if="filteredEmotes.length > 24"
            class="w-full text-center text-xs bg-grey-1 rounded-b border-t border-grey-5 p-2"
          >{{ filteredEmotes.length - 24}} more. Continue typing to refine search...</div>
          <div
            class="w-full text-center text-sm bg-grey-2 rounded-b border-t border-grey-5 px-2 py-1"
          >{{ filteredEmotes[navigatedEmoteIndex]['name'] }}</div>
        </template>

        <div v-else class="text-center text-sm px-2 py-1">No emotes found</div>
      </div>
    </div>
    <p v-if="info && !error" class="text-sm text-blue pl-1">{{ info }}</p>
    <p v-if="error" class="text-sm text-red-8 pl-1">{{ error }}</p>
  </div>
</template>

<script>
import Fuse from "fuse.js"
import tippy from "tippy.js"
import RichTextEmbedModal from "@/components/Modals/RichTextEmbedModal"
import RichTextImageModal from "@/components/Modals/RichTextImageModal"
import RichTextLinkModal from "@/components/Modals/RichTextLinkModal"
import { VideoIcon, ImageIcon, LinkIcon, SmileIcon } from "vue-feather-icons"
import { mapGetters } from "vuex"

import { Editor, EditorContent, EditorMenuBar } from "tiptap"
import {
  Bold,
  Italic,
  Strike,
  Underline,
  BulletList,
  OrderedList,
  ListItem,
  Blockquote,
  History,
  Placeholder,
  HardBreak,
  TrailingNode
} from "tiptap-extensions"
import CustomImage from "./tiptap/image.js"
import Noembed from "./tiptap/noembed.js"
import CustomLink from "./tiptap/link.js"
import Emote from "./tiptap/emote.js"
import Insert from "./tiptap/insert.js"
import ReplyQuote from "./tiptap/replyquote.js"
import ReplyQuoteOwner from "./tiptap/replyquoteowner.js"
// import Spoiler from "./tiptap/spoiler.js"

export default {
  name: "RichTextArea",
  props: {
    value: {
      required: true
    },
    id: {
      required: true
    },
    errors: {
      required: true
    },
    placeholder: {
      default: "Write something …"
    },
    label: {
      default: null
    },
    info: {
      default: null
    },
    simple: {
      default: false
    },
    includeEmotes: {
      default: false
    },
    includeLinks: {
      default: false
    },
    includeImages: {
      default: false
    },
    focus: {
      default: false
    }
  },
  components: {
    EditorContent,
    EditorMenuBar,
    RichTextEmbedModal,
    RichTextImageModal,
    RichTextLinkModal,
    VideoIcon,
    ImageIcon,
    LinkIcon,
    SmileIcon
  },
  watch: {
    value: function(val) {
      if (this.error) this.errors.clear(this.id)
      if (val === "") this.editor.setContent("")
    }
  },
  data() {
    return {
      client: null,
      editor: null,
      labelValid: "text-blue-8",
      labelError: "text-red-8",
      inputValid: "border-grey-5 hover:border-blue-7 focus:border-blue-9",
      inputError: "border-red-5 hover:border-red-7 focus:border-red-9",
      modals: {
        embed: false,
        image: false,
        link: false
      },
      query: null,
      suggestionRange: null,
      filteredEmotes: [],
      navigatedEmoteIndex: 0,
      insertEmote: () => {},
      observer: null
    }
  },
  computed: {
    ...mapGetters({
      emotes: "emotes/list"
    }),
    error() {
      return this.errors.has(this.id) ? this.errors.first(this.id) : false
    },
    hasResults() {
      return this.filteredEmotes.length
    },
    showSuggestions() {
      return this.query || this.hasResults
    },
    visibleEmotes() {
      if (this.filteredEmotes.length < 25) return this.filteredEmotes
      return this.filteredEmotes.slice(0, 24)
    }
  },
  methods: {
    addQuote(html) {
      this.editor.setContent(html + this.value)
    },
    focusEditor() {
      this.editor.focus()
    },
    showLinkModal() {
      this.modals.link = true
    },
    insertImage(src, command) {
      this.modals.image = false
      command({ src })
    },
    insertLink(href, command) {
      this.modals.link = false
      command({ href })
    },
    insertNoembed(html, command) {
      this.modals.embed = false
      command({ html })
    },
    leftHandler() {
      if (this.navigatedEmoteIndex % 6 === 0) {
        let newIndex = this.navigatedEmoteIndex + 5
        if (newIndex < this.filteredEmotes.length) {
          this.navigatedEmoteIndex = newIndex
        } else {
          this.navigatedEmoteIndex = this.filteredEmotes.length - 1
        }
      } else {
        this.navigatedEmoteIndex = this.navigatedEmoteIndex - 1
      }
    },
    rightHandler() {
      if (this.navigatedEmoteIndex % 6 === 5) {
        if (this.navigatedEmoteIndex !== this.filteredEmotes.length - 1) {
          this.navigatedEmoteIndex = this.navigatedEmoteIndex - 5
        } else {
          this.navigatedEmoteIndex =
            Math.floor(this.filteredEmotes.length / 6) * 6 - 6
        }
      } else if (this.navigatedEmoteIndex === this.filteredEmotes.length - 1) {
        this.navigatedEmoteIndex =
          Math.floor(this.filteredEmotes.length / 6) * 6
      } else {
        this.navigatedEmoteIndex = this.navigatedEmoteIndex + 1
      }
    },
    upHandler() {
      if (this.navigatedEmoteIndex < 6) {
        let newIndex =
          Math.floor((this.filteredEmotes.length - 1) / 6) * 6 +
          this.navigatedEmoteIndex
        if (newIndex > this.filteredEmotes.length - 1) {
          this.navigatedEmoteIndex = newIndex - 6
        } else {
          this.navigatedEmoteIndex = newIndex
        }
      } else {
        this.navigatedEmoteIndex = this.navigatedEmoteIndex - 6
      }
    },
    downHandler() {
      let newIndex = this.navigatedEmoteIndex + 6
      if (newIndex > this.filteredEmotes.length - 1) {
        this.navigatedEmoteIndex = this.navigatedEmoteIndex % 6
      } else {
        this.navigatedEmoteIndex = newIndex
      }
    },
    enterHandler() {
      const emote = this.filteredEmotes[this.navigatedEmoteIndex]
      if (emote) {
        this.selectEmote(emote)
      }
    },
    selectEmote(emote) {
      this.insertEmote({
        range: this.suggestionRange,
        attrs: {
          name: emote.name
        }
      })
      this.editor.focus()
    },
    renderPopup(node) {
      if (this.popup) {
        return
      }
      this.popup = tippy(node, {
        content: this.$refs.suggestions,
        trigger: "mouseenter",
        interactive: true,
        theme: "dark",
        placement: "top-start",
        inertia: true,
        duration: [400, 200],
        showOnInit: true,
        arrow: true,
        arrowType: "round"
      })
      if (MutationObserver) {
        this.observer = new MutationObserver(() => {
          this.popup.popperInstance.scheduleUpdate()
        })
        this.observer.observe(this.$refs.suggestions, {
          childList: true,
          subtree: true,
          characterData: true
        })
      }
    },
    destroyPopup() {
      if (this.popup) {
        this.popup.destroy()
        this.popup = null
      }
      if (this.observer) {
        this.observer.disconnect()
      }
    }
  },
  mounted() {
    let extensions = [
      new Placeholder({
        emptyClass: "is-empty",
        emptyNodeText: this.placeholder,
        showOnlyWhenEditable: true
      }),
      new Bold(),
      new Italic(),
      new Strike(),
      new Underline(),
      new History(),
      new HardBreak(),
      new TrailingNode()
    ]

    if (!this.simple) {
      extensions.push(
        new BulletList(),
        new OrderedList(),
        new ListItem(),
        new ReplyQuote(),
        new ReplyQuoteOwner(),
        new Blockquote(),
        new CustomImage(),
        new Noembed(),
        new CustomLink()
        // new Spoiler(),
      )
    }

    if (!this.simple || (this.simple && this.includeEmotes)) {
      extensions.push(
        new Insert(),
        new Emote({
          items: () => this.emotes,
          onEnter: ({ items, query, range, command, virtualNode }) => {
            this.query = query
            this.filteredEmotes = items
            this.suggestionRange = range
            this.renderPopup(virtualNode)
            this.insertEmote = command
          },
          onChange: ({ items, query, range, virtualNode }) => {
            this.query = query
            this.filteredEmotes = items
            this.suggestionRange = range
            this.navigatedEmoteIndex = 0
            this.renderPopup(virtualNode)
          },
          onExit: () => {
            this.query = null
            this.filteredEmotes = []
            this.suggestionRange = null
            this.navigatedEmoteIndex = 0
            this.destroyPopup()
          },
          onKeyDown: ({ event }) => {
            if (event.keyCode === 37) {
              this.leftHandler()
              return true
            }
            if (event.keyCode === 38) {
              this.upHandler()
              return true
            }
            if (event.keyCode === 39) {
              this.rightHandler()
              return true
            }
            if (event.keyCode === 40) {
              this.downHandler()
              return true
            }
            if (event.keyCode === 13) {
              this.enterHandler()
              return true
            }
            return false
          },
          onFilter: (items, query) => {
            if (query === "") {
              return items
            }
            const fuse = new Fuse(items, {
              threshold: 0.2,
              keys: ["name"]
            })
            return fuse.search(query)
          }
        })
      )
    }

    if (!this.simple || (this.simple && this.includeLinks)) {
      extensions.push(new CustomLink())
    }

    if (!this.simple || (this.simple && this.includeImages)) {
      extensions.push(new CustomImage())
    }

    this.editor = new Editor({
      extensions: extensions,
      dropCursor: {
        width: 2,
        color: "#ccc"
      },
      content: this.value,
      onUpdate: ({ getHTML }) => {
        this.$emit("input", getHTML())
      },
      autoFocus: this.focus ? "end" : false
    })
  },
  beforeDestroy() {
    this.editor.destroy()
  }
}
</script>
